import React from "react"
import {
	Box,
	Button, FormControl,
	Grid,
	IconButton, InputLabel, MenuItem, Select, TextField, Tooltip,
	Typography,
} from "@mui/material"

import { t, Trans } from "@lingui/macro"

import Icon from "@mdi/react"
import {
	mdiClockTimeEightOutline,
	mdiCheckboxMarkedCircleOutline, mdiAccountOutline, mdiSkipNext, mdiTrashCanOutline
} from "@mdi/js"

import { Editor } from "react-draft-wysiwyg"
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"

import {
	Action, ActionStatus, ActionUtils, UpdateActionRequest,
	WorkflowStepType,
} from "models"
import { SbTab, SbTabList } from "../../commons"
import { TabContext } from "@mui/lab"
import { convertFromRaw, convertToRaw, EditorState } from "draft-js"
import { WorkflowService } from "../../../services/workflow.service"
import { RestResponse, withUseFormHook } from "@sinossi/mates-react-library"
import withUseRouter, { UseRouterParams } from "../../../services/router"
import { MatesComponent, MatesComponentState } from "../../mates-component"
import { UseFormReturn } from "react-hook-form"

interface SecondColumnProps extends UseRouterParams, UseFormReturn
{
    action: Action | null
	refresh: (newContent: Action) => void
	openDeferDialog: (data: Action) => void
	openMarkAsDone: (data: Action) => void
	openAssignDialog: (data: Action) => void
	openSkipDialog: (data: Action) => void
}
interface SecondColumnState extends MatesComponentState
{
	editorState: EditorState
	actionName: string
	actionType: WorkflowStepType
	loading: boolean
}
class SecondColumn extends React.Component<SecondColumnProps, SecondColumnState>
{
	private readonly matesComponent: MatesComponent<SecondColumnProps, SecondColumnState> = new MatesComponent(this)

	constructor(props: SecondColumnProps)
	{
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			editorState: EditorState.createEmpty(),
			actionName: "",
			actionType: WorkflowStepType.NOTIFICATION,
			loading: true
		}
	}

	componentDidMount() {
		this.initForm()
	}

	componentDidUpdate(prevProps: Readonly<SecondColumnProps>, prevState: Readonly<SecondColumnState>, snapshot?: any) {
		if(prevProps.action !== this.props.action){
			this.initForm()
		}
	}

	private initForm = (): void => {
		if(this.props.action){
			const contentState = convertFromRaw(this.props.action.content)
			this.setState(() => ({
				editorState: EditorState.createWithContent(contentState),
				actionName: this.props.action!.name,
				actionType: this.props.action!.actionType,
				loading: false
			}))
		}
	}

	private handleEditorChange = (editorState: any) => {
		this.setState(() => ({
			editorState: editorState,
		}))
	}

	private handleNameChange = (event: any) => {
		this.setState(() => ({
			actionName: event.target.value
		}))
	}

	private handleActionTypeChange = (event: any) => {
		const newValue = ActionUtils.renderObject(event.target.value)

		if(newValue){
			this.setState(() => ({
				actionType: newValue
			}))
		}
	}

	private saveContent = () => {
		let request: UpdateActionRequest = {
			content: convertToRaw(this.state.editorState.getCurrentContent()),
			name: this.state.actionName,
			actionType: this.state.actionType
		}

		if(this.props.action){
			this.setState(
				() => WorkflowService.updateActionContent(
					this.props.action!.id,
					request,
					this.updateActionContentCallback,
					this.matesComponent.onException)
			)
		}

	}

	private updateActionContentCallback = (response: RestResponse<any, any>) => {
		if(response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		if(response.data){
			this.props.refresh(response.data)
		}
	}

    render()
	{
		let selectedActionString = ActionUtils.renderString(this.state.actionType)

		return (
            <>
                {
					!this.state.loading && this.props.action &&
                    <>
					<TabContext value={"1"}>
						<Box sx={{ borderBottom: 0 }}>
							<SbTabList variant="fullWidth">
								<SbTab
									disableRipple
									disableTouchRipple
									label={t({ id: "actions.details", message: "Dettagli azione", })}
									value="1" />
							</SbTabList>
						</Box>
					</TabContext>
						<Box sx={{mr: 3}}>
							<Grid container overflow="auto" rowSpacing={3}>
								<Grid item xs={12} sx={{mt: 3}}>
									<TextField variant="outlined"
											   label={t({
												   id: "actions.second-column.action-name",
												   message: "Nome azione",
											   })}
											   name="actionName"
											   value={this.state.actionName}
											   onChange={this.handleNameChange}
									/>
								</Grid>
								<Grid item xs={12}>
									<Editor
										readOnly={this.props.action.status !== ActionStatus.WORKING && this.props.action.status !== ActionStatus.PENDING}
										editorState={this.state.editorState}
										onEditorStateChange={this.handleEditorChange}
										toolbar={{
											options: ['inline', 'list', 'link'],
											inline: {
												options: ['bold', 'italic'],
											},

											list: {
												options: ['unordered', 'ordered'],
											},
											link: {
												options: ['link'],
											},
										}}
										toolbarStyle={{
											backgroundColor: "#11347B",
											borderColor: "#11347B"
										}}
										editorStyle={{
											minHeight: "32vh",
											maxHeight: "32vh",
											border: "1px solid lightgray",
											overflow: "auto",
										}}
									/>
								</Grid>
							</Grid>
							<Grid container sx={{mt: 3}}>
								<Grid item xs={12} md={5}>
									<FormControl>
										<InputLabel id="actionTypeLabel">{t({id: "workflow.column-three.type-label", message: "Tipo di azione"})}</InputLabel>
										<Select variant="outlined"
												labelId="actionTypeLabel"
												label={t({id: "workflow.column-three.type-label", message: "Tipo di azione"})}
												name="actionType"
												onChange={this.handleActionTypeChange}
												value={selectedActionString}
												sx={{width: "200px"}}
										>
											{Object.keys(WorkflowStepType).map((typeKey, index) =>
												<MenuItem value={typeKey} key={typeKey}>
													<Trans id={ `enums.workflow-step-types.${typeKey}`}>

													</Trans>
												</MenuItem>
											)}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} md={7}>
									<Box width="100%" textAlign="right">
										{(this.props.action.status !== ActionStatus.DONE && this.props.action.status !== ActionStatus.SKIPPED) &&
											<>
												<Tooltip title="Assegna" placement="top" arrow>
													<IconButton
														sx={{
															color: 'grey',
															backgroundColor: "transparent",
															":hover": {
																color: "secondary.main"
															}
														}}
														aria-label="assign"
														onClick={() => this.props.openAssignDialog(this.props.action!)}
														disableFocusRipple
														disableRipple>
														<Icon path={mdiAccountOutline} size={1} />
													</IconButton>
												</Tooltip>
												<Tooltip title="Posticipa" placement="top" arrow>
													<IconButton
														sx={{
															color: 'grey',
															backgroundColor: "transparent",
															":hover": {
																color: "secondary.main"
															}
														}}
														aria-label="postpone"
														onClick={() => this.props.openDeferDialog(this.props.action!)}
														disableFocusRipple
														disableRipple>
														<Icon path={mdiClockTimeEightOutline} size={1} />
													</IconButton>
												</Tooltip>
											</>
										}
										{this.props.action.status === ActionStatus.WORKING &&
											<>
												{
													this.props.action.workflow &&
													<Tooltip title="Salta" placement="top" arrow>
														<IconButton
															sx={{
																color: 'grey',
																backgroundColor: "transparent",
																":hover": {
																	color: "secondary.main"
																}
															}}
															aria-label="skip"
															onClick={() => this.props.openSkipDialog(this.props.action!)}
															disableFocusRipple
															disableRipple>
															<Icon path={mdiSkipNext} size={1} />
														</IconButton>
													</Tooltip>
												}
												{
													!this.props.action.workflow &&
													<Tooltip title="Elimina" placement="top" arrow>
														<IconButton
															sx={{
																color: 'grey',
																backgroundColor: "transparent",
																":hover": {
																	color: "secondary.main"
																}
															}}
															aria-label="delete"
															onClick={() => this.props.openSkipDialog(this.props.action!)}
															disableFocusRipple
															disableRipple>
															<Icon path={mdiTrashCanOutline} size={1} />
														</IconButton>
													</Tooltip>
												}
												<Tooltip title="Eseguita" placement="top" arrow>
													<IconButton
														sx={{
															color: 'grey',
															backgroundColor: "transparent",
															":hover": {
																color: "secondary.main"
															}
														}}
														aria-label="Delete"
														onClick={() => this.props.openMarkAsDone(this.props.action!)}
														disableFocusRipple
														disableRipple>
														<Icon path={mdiCheckboxMarkedCircleOutline} size={1} />
													</IconButton>
												</Tooltip>
											</>
										}
										{(this.props.action.status !== ActionStatus.DONE && this.props.action.status !== ActionStatus.SKIPPED) &&
											<Button variant="contained"
													type="button"
													disableElevation
													sx={{
														textTransform: "none",
														borderColor: "primary.main",
														bgcolor: "primary.main",
														paddingY: 0,
														paddingX: 2
													}}
													onClick={this.saveContent}
											>
												<Typography>
													<Trans id="actions.save">
														Salva
													</Trans>
												</Typography>
											</Button>
										}
									</Box>
								</Grid>
							</Grid>
						</Box>
					</>
                }
            </>
        )
    }
}

export default withUseRouter(
	withUseFormHook<{}>(
		SecondColumn,
		{},
		{}
	))
